import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Leaf, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import SocialLoginButtons from "../components/SocialLoginButtons";
import ContinueAsGuestButton from "../components/ContinueAsGuestButton";

export default function LoginPage() {
  const { login, loginVerify2FA } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 2FA challenge state — set when /auth/login returns requires_2fa.
  const [twoFA, setTwoFA] = useState(null); // { totp_token, ttl_s, expires_at }
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Tick the TTL countdown for the 2FA window.
  useEffect(() => {
    if (!twoFA?.expires_at) return;
    const tick = () => {
      const s = Math.max(0, Math.round((twoFA.expires_at - Date.now()) / 1000));
      setSecondsLeft(s);
      if (s <= 0) {
        setTwoFA(null);
        setError("Your login window expired — please sign in again.");
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [twoFA]);

  const finishLogin = (user) => {
    toast.success(`Welcome back, ${user.name}`);
    const dest = user.role === "admin" ? "/admin" : (location.state?.from?.pathname || "/dashboard");
    navigate(dest, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.requires_2fa) {
      setTwoFA({
        totp_token: res.totp_token,
        ttl_s: res.ttl_s,
        expires_at: Date.now() + (res.ttl_s || 300) * 1000,
      });
      setCode("");
      return;
    }
    finishLogin(res.user);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFA?.totp_token) return;
    setError("");
    setLoading(true);
    const res = await loginVerify2FA(twoFA.totp_token, code);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    finishLogin(res.user);
  };

  const startOver = () => {
    setTwoFA(null);
    setCode("");
    setError("");
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-700 text-white mb-3">
            {twoFA ? <ShieldCheck className="w-6 h-6" /> : <Leaf className="w-6 h-6" />}
          </span>
          <h1 className="font-display text-3xl text-emerald-950 font-light tracking-tight">
            {twoFA ? "Two-step verification" : "Welcome back"}
          </h1>
          <p className="text-emerald-900/60 mt-2 text-sm">
            {twoFA
              ? "Enter the 6-digit code from your authenticator app to continue."
              : "Continue your health journey."}
          </p>
        </div>
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            {twoFA ? (
              <form onSubmit={handle2FASubmit} className="space-y-4" data-testid="login-2fa-form">
                <div>
                  <Label htmlFor="code" className="text-emerald-900">Authenticator code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ""))}
                    className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30 tracking-[0.4em] text-center font-mono text-lg"
                    placeholder="123 456"
                    maxLength={9}
                    data-testid="login-2fa-code-input"
                  />
                  <p className="mt-2 text-xs text-emerald-900/60 leading-relaxed">
                    Enter the rolling 6-digit code, or use a backup code (XXXX-XXXX). Window closes in <span className="font-semibold text-emerald-800">{secondsLeft}s</span>.
                  </p>
                </div>
                {error ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="login-2fa-error">
                    {error}
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={loading || !code || code.length < 6}
                  className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6"
                  data-testid="login-2fa-submit-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {loading ? "Verifying…" : "Verify and sign in"}
                </Button>
                <button
                  type="button"
                  onClick={startOver}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-emerald-700 hover:underline pt-1"
                  data-testid="login-2fa-back-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Use a different account
                </button>
              </form>
            ) : (
              <>
                <SocialLoginButtons intent="signin" />
                <div className="mt-3">
                  <ContinueAsGuestButton />
                </div>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                  <Label htmlFor="email" className="text-emerald-900">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                    placeholder="you@example.com"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-emerald-900">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                {error ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="login-error">
                    {error}
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <p className="text-center text-sm">
                  <Link to="/forgot-password" className="text-emerald-700 font-semibold hover:underline" data-testid="login-forgot-password-link">
                    Forgot your password?
                  </Link>
                </p>
              </form>
              </>
            )}
            {!twoFA ? (
              <p className="mt-6 text-sm text-emerald-900/70 text-center">
                New to Feel Greats?{" "}
                <Link to="/register" className="text-emerald-700 font-semibold hover:underline" data-testid="login-to-register-link">
                  Create an account
                </Link>
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
